import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import app from '../firebase/config';
import { smartDataFetcher } from './smartDataFetcher';
import { rateLimitService } from './rateLimitService';

// Initialize Firebase services
const functions = getFunctions(app);
const db = getFirestore(app);

export interface BusinessQuery {
  query: string;
  tenantId: string;
  context: {
    businessName: string;
    businessType: string;
    timezone: string;
  };
}

export interface BusinessMetrics {
  todaySales: number;
  todayOrders: number;
  popularItems: Array<{name: string, quantity: number, revenue: number}>;
  lowStockItems: Array<{name: string, currentStock: number, minStock: number}>;
  topCustomers: Array<{name: string, totalSpent: number, orderCount: number}>;
  revenueTrends: Array<{date: string, revenue: number}>;
}

export interface AIResponse {
  content: string;
  confidence: number;
  dataUsed: string[];
  suggestions?: string[];
}

class FirebaseAIService {
  private isInitialized = false;

  constructor() {
    this.isInitialized = true;
  }

  async processBusinessQuery(query: string, tenantId: string, businessData: any): Promise<AIResponse> {
    try {
      // Check rate limits
      const [tenantLimit, sessionLimit] = await Promise.all([
        rateLimitService.checkTenantLimit(tenantId),
        rateLimitService.checkSessionLimit(businessData.sessionId || 'default')
      ]);

      if (!tenantLimit.allowed) {
        return {
          content: `🚫 **Rate Limit Exceeded**\n\nYou've reached your hourly query limit (${rateLimitService.getConfig().tenantLimit} queries/hour). Please try again in ${Math.ceil((tenantLimit.resetTime - Date.now()) / 60000)} minutes.`,
          confidence: 1.0,
          dataUsed: ['Rate Limit Check'],
          suggestions: ['Wait a few minutes', 'Contact support for higher limits']
        };
      }

      if (!sessionLimit.allowed) {
        return {
          content: `🚫 **Session Rate Limit**\n\nToo many queries in a short time. Please wait ${Math.ceil((sessionLimit.resetTime - Date.now()) / 60000)} minutes before asking more questions.`,
          confidence: 1.0,
          dataUsed: ['Session Rate Limit Check'],
          suggestions: ['Wait a few minutes', 'Start a new chat session']
        };
      }

      // Use smart data fetching
      const smartData = await smartDataFetcher.fetchRelevantData(tenantId, query);

      // Call Firebase Cloud Function with real Gemini API
      try {
        const generateContent = httpsCallable(functions, 'generateContent');
        
        // Use smart data fetching for optimized performance
        const requestPayload = {
          contents: [
            {
              role: 'user',
              parts: [
                {
                  text: `You are an AI business assistant for CafeLawn, a comprehensive multi-tenant cafe management system. You have access to relevant business data for tenant ID: ${tenantId}.

BUSINESS CONTEXT:
- Business Type: Cafe/Restaurant
- Currency: Indian Rupees (₹)
- Timezone: Asia/Kolkata
- Current Time: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}

RELEVANT BUSINESS DATA (${smartData.dataType}):
${this.formatDataForPrompt(smartData.data)}

BUSINESS QUERY: "${query}"

Please provide a helpful response based on the business data provided above. Focus on:
1. Specific insights from the actual data
2. Actionable recommendations
3. Professional and encouraging tone
4. Use Indian Rupees (₹) for all monetary values
5. Reference specific data points when possible

If the data doesn't contain enough information to fully answer the query, suggest what additional information would be helpful.`
                }
              ]
            }
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048, // Increased for more detailed responses
          },
          safetySettings: [
            {
              category: 'HARM_CATEGORY_HARASSMENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE'
            },
            {
              category: 'HARM_CATEGORY_HATE_SPEECH',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE'
            },
            {
              category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE'
            },
            {
              category: 'HARM_CATEGORY_DANGEROUS_CONTENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE'
            }
          ]
        };

        const result = await generateContent(requestPayload);
        
        const aiResponse = (result.data as any)?.candidates?.[0]?.content?.parts?.[0]?.text || 
                          'Sorry, I could not process your request.';

        return {
          content: aiResponse,
          confidence: 0.9,
          dataUsed: ['🔥 Real Gemini AI + Firestore Data'],
          suggestions: this.generateSuggestions(query)
        };
      } catch (cloudFunctionError: any) {
        // Fall through to mock response
      }
      
      // FALLBACK: If cloud function fails, use enhanced mock response based on actual business data
      return {
        content: this.getMockResponse(query, businessData),
        confidence: 0.8,
        dataUsed: ['Business Intelligence Data (Fallback)'],
        suggestions: this.generateSuggestions(query)
      };

    } catch (error) {
      // Fallback to basic response
      try {
        return {
          content: this.getFallbackResponse(query),
          confidence: 0.5,
          dataUsed: ['Fallback Data'],
          suggestions: this.generateSuggestions(query)
        };
      } catch (mockError) {
        return {
          content: this.getFallbackResponse(query),
          confidence: 0.5,
          dataUsed: [],
          suggestions: ['Try asking about sales, inventory, or customers']
        };
      }
    }
  }

  private createSystemPrompt(businessData: any): string {
    const currentTime = new Date();
    const hour = currentTime.getHours();
    const isBusinessHours = hour >= 6 && hour <= 22;
    const isMorning = hour >= 6 && hour <= 12;
    const isAfternoon = hour >= 12 && hour <= 17;
    const isEvening = hour >= 17 && hour <= 22;
    
    let timeContext = '';
    if (isMorning) timeContext = 'Good morning! ';
    else if (isAfternoon) timeContext = 'Good afternoon! ';
    else if (isEvening) timeContext = 'Good evening! ';
    else timeContext = 'Good night! ';
    
    const businessStatus = isBusinessHours ? '🟢 Open for business' : '🔴 Outside business hours';
    
    return `You are an AI business assistant for CafeLawn, a comprehensive multi-tenant cafe management system. 

${timeContext}I'm here to help you understand your business data and make informed decisions.

BUSINESS STATUS: ${businessStatus}
Current time: ${currentTime.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}

BUSINESS DOMAIN KNOWLEDGE:
CafeLawn is designed for cafes, restaurants, and food service businesses. The system handles:
- Multi-tenant architecture with complete data isolation
- Real-time order management with kitchen workflow (requested → preparing → completed)
- Automatic inventory deduction when menu items are sold
- Customer relationship management with spending analytics
- Payment processing with multiple methods (cash, card, digital, UPI)
- Table management with QR code ordering
- Sophisticated inventory with unit conversions (boxes to pieces, etc.)
- Role-based access control for staff management

Your role is to help business owners understand their data and make informed decisions. You have access to real-time business data including:

SALES DATA:
- Today's sales: ₹${businessData.todaySales || 0}
- Today's orders: ${businessData.todayOrders || 0}
- Yesterday's sales: ₹${businessData.yesterdaySales || 0}
- Yesterday's orders: ${businessData.yesterdayOrders || 0}
- Total revenue: ₹${businessData.totalRevenue || 0}
- Average order value: ₹${businessData.averageOrderValue || 0}

YESTERDAY'S TOP SPENDER:
${businessData.yesterdayTopSpender ? `- ${businessData.yesterdayTopSpender.name}: ₹${businessData.yesterdayTopSpender.amount}` : 'No sales yesterday'}

INVENTORY DATA:
- Low stock items: ${businessData.lowStockItems?.length || 0} items need restocking
- Inventory value: ₹${businessData.inventoryValue || 0}

CUSTOMER DATA:
- Active customers: ${businessData.activeCustomers || 0}
- Total customers: ${businessData.totalCustomers || 0}

POPULAR ITEMS:
${businessData.popularItems?.map((item: any) => `- ${item.name}: ${item.quantity} sold, ₹${item.revenue} revenue`).join('\n') || 'No data available'}

LOW STOCK ALERTS:
${businessData.lowStockItems?.map((item: any) => `- ${item.name}: ${item.currentStock} remaining (min: ${item.minStock})`).join('\n') || 'No low stock items'}

TOP CUSTOMERS:
${businessData.topCustomers?.map((customer: any) => `- ${customer.name}: ₹${customer.totalSpent} spent, ${customer.orderCount} orders`).join('\n') || 'No customer data'}

UNPAID CUSTOMERS:
${businessData.unpaidCustomers?.map((customer: any) => `- ${customer.name}: ₹${customer.unpaidAmount} unpaid (${customer.unpaidOrders} orders)`).join('\n') || 'No unpaid customers'}

UNPAID AMOUNT SUMMARY:
- Total unpaid amount: ₹${businessData.totalUnpaidAmount || 0}
- Number of unpaid customers: ${businessData.unpaidCustomers?.length || 0}
- Number of unpaid orders: ${businessData.unpaidOrders?.length || 0}

Guidelines:
1. Be helpful, professional, and concise
2. Use Indian Rupees (₹) for all monetary values
3. Provide actionable insights when possible
4. If data is not available, suggest what information would be helpful
5. Keep responses conversational but informative
6. Use emojis sparingly but appropriately
7. Always be encouraging and supportive of the business owner
8. When asked about "yesterday", use the yesterday's data if available
9. When asked about "today" and no sales, suggest checking yesterday's data or overall top customers
10. Consider the time of day - if it's morning, focus on preparation and planning
11. If it's evening, focus on closing and next day preparation
12. If outside business hours, mention this and suggest checking data from business hours
13. Always provide context about what the data means for the business
14. Suggest next steps or actions when appropriate`;
  }

  private createUserMessage(query: string, businessData: any): string {
    return `Business Query: "${query}"

Please provide a helpful response based on the business data provided above. Focus on actionable insights and be encouraging to the business owner.`;
  }

  private extractDataUsed(query: string, businessData: any): string[] {
    const dataUsed: string[] = [];
    const queryLower = query.toLowerCase();

    if (queryLower.includes('sale') || queryLower.includes('revenue') || queryLower.includes('income')) {
      dataUsed.push('Sales Data');
    }
    if (queryLower.includes('order') || queryLower.includes('transaction')) {
      dataUsed.push('Order Data');
    }
    if (queryLower.includes('inventory') || queryLower.includes('stock') || queryLower.includes('item')) {
      dataUsed.push('Inventory Data');
    }
    if (queryLower.includes('customer') || queryLower.includes('client')) {
      dataUsed.push('Customer Data');
    }
    if (queryLower.includes('popular') || queryLower.includes('best') || queryLower.includes('top')) {
      dataUsed.push('Popular Items');
    }
    if (queryLower.includes('low') || queryLower.includes('out of stock')) {
      dataUsed.push('Low Stock Alerts');
    }

    return dataUsed.length > 0 ? dataUsed : ['General Business Data'];
  }

  private generateSuggestions(query: string): string[] {
    const queryLower = query.toLowerCase();
    const suggestions = [];

    if (queryLower.includes('sale') || queryLower.includes('revenue')) {
      suggestions.push('What are today\'s sales?', 'Show me revenue trends', 'Which items are selling best?');
    } else if (queryLower.includes('inventory') || queryLower.includes('stock')) {
      suggestions.push('Show me low stock items', 'What\'s our inventory value?', 'Which items need restocking?');
    } else if (queryLower.includes('customer')) {
      suggestions.push('Who are our top customers?', 'Show me customer analytics', 'How many active customers do we have?');
    } else {
      suggestions.push('What are today\'s sales?', 'Show me low stock items', 'Who are our top customers?', 'What\'s our revenue trend?');
    }

    return suggestions;
  }

  private getMockResponse(query: string, businessData: any): string {
    const queryLower = query.toLowerCase();

    // Sales related queries
    if (queryLower.includes('today') && (queryLower.includes('sale') || queryLower.includes('revenue'))) {
      return `📊 **Today's Sales Summary**\n\n💰 **Revenue**: ₹${businessData.todaySales || 0}\n📦 **Orders**: ${businessData.todayOrders || 0}\n\n${businessData.todaySales > 0 ? 'Great job! Keep up the momentum! 🚀' : 'No sales yet today. Consider checking yesterday\'s performance for insights.'}`;
    }

    if (queryLower.includes('yesterday') && (queryLower.includes('sale') || queryLower.includes('revenue'))) {
      return `📊 **Yesterday's Sales Summary**\n\n💰 **Revenue**: ₹${businessData.yesterdaySales || 0}\n📦 **Orders**: ${businessData.yesterdayOrders || 0}\n\n${businessData.yesterdayTopSpender ? `🏆 **Top Spender**: ${businessData.yesterdayTopSpender.name} (₹${businessData.yesterdayTopSpender.amount})` : 'No sales recorded yesterday.'}`;
    }

    if (queryLower.includes('total') && (queryLower.includes('sale') || queryLower.includes('revenue'))) {
      return `📊 **Total Revenue Overview**\n\n💰 **Total Revenue**: ₹${businessData.totalRevenue || 0}\n📦 **Total Orders**: ${businessData.totalOrders || 0}\n📈 **Average Order Value**: ₹${businessData.averageOrderValue || 0}\n\nThis represents your overall business performance! 📈`;
    }

    // Inventory related queries
    if (queryLower.includes('low stock') || queryLower.includes('out of stock') || queryLower.includes('restock')) {
      const lowStockCount = businessData.lowStockItems?.length || 0;
      if (lowStockCount > 0) {
        const items = businessData.lowStockItems.slice(0, 5).map((item: any) => 
          `• ${item.name}: ${item.currentStock} remaining (min: ${item.minStock})`
        ).join('\n');
        return `⚠️ **Low Stock Alert**\n\nYou have ${lowStockCount} items that need restocking:\n\n${items}\n\n${lowStockCount > 5 ? `...and ${lowStockCount - 5} more items` : ''}\n\n💡 **Action Required**: Please restock these items to avoid stockouts!`;
      } else {
        return `✅ **Inventory Status: Good**\n\nAll items are well-stocked! No low stock alerts at the moment. 🎉`;
      }
    }

    if (queryLower.includes('inventory') && queryLower.includes('value')) {
      return `📦 **Inventory Value**\n\n💰 **Total Inventory Value**: ₹${businessData.inventoryValue || 0}\n\nThis represents the total value of all items in your inventory. 📊`;
    }

    // Customer related queries
    if (queryLower.includes('top customer') || queryLower.includes('best customer') || queryLower.includes('who are our top customers')) {
      const topCustomers = businessData.topCustomers?.slice(0, 5) || [];
      if (topCustomers.length > 0) {
        const customers = topCustomers.map((customer: any) => 
          `• ${customer.name}: ₹${customer.totalSpent} (${customer.orderCount} orders)`
        ).join('\n');
        return `🏆 **Top Customers**\n\nYour most valuable customers:\n\n${customers}\n\n💡 **Tip**: Consider loyalty programs or special offers for these customers!`;
      } else {
        return `📊 **Customer Data**\n\nNo customer data available yet. As you process more orders, this information will become available.`;
      }
    }

    if (queryLower.includes('active customer') || queryLower.includes('customer count')) {
      return `👥 **Customer Overview**\n\n👤 **Active Customers**: ${businessData.activeCustomers || 0}\n📊 **Total Customers**: ${businessData.totalCustomers || 0}\n\nActive customers are those who have placed orders in the last 30 days. 📈`;
    }

    // Popular items queries
    if (queryLower.includes('popular') || queryLower.includes('best selling') || queryLower.includes('top item') || queryLower.includes('selling best')) {
      const popularItems = businessData.popularItems?.slice(0, 5) || [];
      if (popularItems.length > 0) {
        const items = popularItems.map((item: any) => 
          `• ${item.name}: ${item.quantity} sold (₹${item.revenue} revenue)`
        ).join('\n');
        return `🔥 **Popular Items**\n\nYour best-selling items:\n\n${items}\n\n💡 **Insight**: These items are driving your revenue. Consider promoting them further!`;
      } else {
        return `📊 **Popular Items**\n\nNo sales data available yet. As you process more orders, popular items will be identified.`;
      }
    }

    // Unpaid customers queries (CHECK THIS FIRST - highest priority)
    if (queryLower.includes('unpaid') || 
        queryLower.includes('pending payment') ||
        queryLower.includes("haven't paid") ||
        queryLower.includes("havent paid") ||
        queryLower.includes("hasnt paid") ||
        queryLower.includes("hasn't paid") ||
        queryLower.includes("who haven") ||
        queryLower.includes("didnt pay") ||
        queryLower.includes("didn't pay") ||
        queryLower.includes('not paid') ||
        queryLower.includes('who owes') ||
        queryLower.includes('payment due')) {
      
      console.log('🔍 Unpaid query detected, businessData:', businessData.unpaidCustomers);
      
      const unpaidCount = businessData.unpaidCustomers?.length || 0;
      if (unpaidCount > 0) {
        const unpaidList = businessData.unpaidCustomers
          .slice(0, 10)
          .map((customer: any, index: number) => 
            `${index + 1}. **${customer.name}** ${customer.phone ? `(📞 ${customer.phone})` : ''}\n   💰 Unpaid: ₹${customer.unpaidAmount.toFixed(2)}\n   📦 Order: #${customer.orderId?.slice(-8)}\n   📅 Date: ${customer.orderDate ? new Date(customer.orderDate).toLocaleDateString() : 'Unknown'}`
          )
          .join('\n\n');
        
        return `💰 **Customers Who Haven't Paid**\n\n${unpaidList}\n\n**📊 Summary:**\n• Total unpaid amount: ₹${businessData.totalUnpaidAmount?.toFixed(2) || 0}\n• Number of customers: ${unpaidCount}\n• Total unpaid orders: ${businessData.unpaidOrders?.length || 0}\n\n💡 **Action Required**: Follow up with these customers to collect payments!`;
      } else {
        return `✅ **All Payments Collected!**\n\n• No unpaid customers found\n• All orders are fully paid\n• Great payment management! 🎉\n\n${businessData.totalRevenue > 0 ? `💰 Total revenue collected: ₹${businessData.totalRevenue.toFixed(2)}` : ''}`;
      }
    }

    // Business overview queries (broad matching)
    if (queryLower.includes('business') || 
        (queryLower.includes('how') && (queryLower.includes('going') || queryLower.includes('doing'))) ||
        queryLower.includes('overview') || 
        queryLower.includes('summary') ||
        queryLower.includes('status')) {
      
      const summary = `📊 **Business Overview**

💰 **Today's Performance:**
• Sales: ₹${(businessData.todaySales || 0).toFixed(2)}
• Orders: ${businessData.todayOrders || 0}
• Average Order: ₹${(businessData.averageOrderValue || 0).toFixed(2)}

📈 **Overall Stats:**
• Total Revenue: ₹${(businessData.totalRevenue || 0).toFixed(2)}
• Total Orders: ${businessData.totalOrders || 0}
• Total Customers: ${businessData.totalCustomers || 0}
• Active Customers: ${businessData.activeCustomers || 0}

${businessData.unpaidCustomers?.length > 0 ? `💰 **Unpaid Amounts:**\n• ${businessData.unpaidCustomers.length} customers owe ₹${(businessData.totalUnpaidAmount || 0).toFixed(2)}\n` : '✅ **Payments**: All paid up!\n'}
${businessData.lowStockItems?.length > 0 ? `⚠️ **Inventory Alert:**\n• ${businessData.lowStockItems.length} items need restocking\n` : '✅ **Inventory**: Well-stocked\n'}
${businessData.popularItems?.length > 0 ? `🔥 **Top Item**: ${businessData.popularItems[0]?.name} (${businessData.popularItems[0]?.quantity} sold)\n` : ''}

💡 **Business Health**: ${businessData.todaySales > 0 ? 'Active and performing well! 🚀' : 'Waiting for today\'s first sale.'}`;
      
      return summary;
    }

    // Menu items queries
    if (queryLower.includes('menu') || queryLower.includes('item')) {
      return `📋 **Menu Items**\n\nYou have ${businessData.popularItems?.length || 0} items in your menu.\n\n${businessData.popularItems?.length > 0 ? 'Popular items include:\n' + businessData.popularItems.slice(0, 3).map((item: any) => `• ${item.name}`).join('\n') : 'No menu items data available yet.'}\n\n💡 **Tip**: Keep your menu updated with current prices and availability!`;
    }

    // Revenue trend queries
    if (queryLower.includes('trend') || queryLower.includes('performance')) {
      const todayVsYesterday = (businessData.todaySales || 0) - (businessData.yesterdaySales || 0);
      const trend = todayVsYesterday > 0 ? '📈 Improving' : todayVsYesterday < 0 ? '📉 Declining' : '➡️ Stable';
      return `📈 **Revenue Trend Analysis**\n\n💰 **Today**: ₹${businessData.todaySales || 0}\n💰 **Yesterday**: ₹${businessData.yesterdaySales || 0}\n📊 **Trend**: ${trend}\n\n${trend.includes('Improving') ? 'Great performance! Keep up the momentum! 🚀' : trend.includes('Declining') ? 'Consider reviewing your strategies and promotions.' : 'Business is stable. Look for growth opportunities!'}`;
    }

    // Non-business queries (weather, greetings, etc.)
    if (queryLower.includes('weather') || 
        queryLower.includes('hello') || 
        queryLower.includes('hi ') ||
        queryLower.match(/^hi$/)||
        queryLower.includes('how are you')) {
      return `👋 **Hello!**\n\nI'm your business AI assistant for CafeLawn! I'm here to help you understand and manage your business better.\n\n**I can answer questions about:**\n• 📊 Sales, revenue, and profit\n• 👥 Customers and their spending\n• 💰 Unpaid bills and payments\n• 📦 Inventory and stock levels  \n• 🍽️ Menu items and popularity\n• 📈 Business analytics and trends\n\n**Popular questions:**\n• "Who hasn't paid?"\n• "What are today's sales?"\n• "Show me top customers"\n• "Which items are low on stock?"\n• "What's my profit this week?"\n\nHow can I help with your business today? 💼`;
    }

    // Default response with helpful suggestions
    return `🤖 **AI Business Assistant**\n\nI can help you with:\n\n📊 **Sales Analytics**: "What are today's sales?"\n📦 **Inventory Management**: "Show me low stock items"\n👥 **Customer Insights**: "Who are our top customers?"\n💰 **Unpaid Bills**: "Who hasn't paid?"\n📈 **Revenue Tracking**: "What's our revenue trend?"\n📋 **Menu Items**: "Show me popular items"\n\n💡 **Try asking a business question!** I'll analyze your real data and provide insights.`;
  }



  private getFallbackResponse(query: string): string {
    return `🤖 **AI Assistant**\n\nI'm here to help with your business insights! Try asking me about:\n\n• Today's sales and revenue\n• Inventory and stock levels\n• Customer analytics\n• Popular items\n• Business trends\n\n💡 **Example**: "What are today's sales?"`;
  }

  /**
   * Format data for AI prompt (compact format)
   */
  private formatDataForPrompt(data: any): string {
    if (!data) return 'No data available';

    const sections: string[] = [];

    // Format orders
    if (data.orders && data.orders.length > 0) {
      const orderLines = data.orders.slice(0, 20).map((order: any) => 
        `#${order.id?.slice(-6) || 'N/A'}|${order.customer}|${this.maskPhone(order.customerPhone)}|${this.compressItems(order.items)}|₹${order.total || 0}|${this.formatCompactDate(order.createdAt)}|${order.isPaid ? '✓paid' : '⚠unpaid₹' + (order.remainingAmount || 0)}`
      );
      sections.push(`ORDERS (${data.orders.length} total):\n${orderLines.join('\n')}`);
    }

    // Format customers
    if (data.customers && data.customers.length > 0) {
      const customerLines = data.customers.slice(0, 10).map((customer: any) =>
        `${customer.name}|${this.maskPhone(customer.phone)}|₹${customer.totalSpent || 0}spent|${customer.totalOrders || 0}orders|last:${this.formatCompactDate(customer.lastOrderDate)}`
      );
      sections.push(`CUSTOMERS (${data.customers.length} total):\n${customerLines.join('\n')}`);
    }

    // Format popular items
    if (data.popularItems && data.popularItems.length > 0) {
      const itemLines = data.popularItems.map((item: any) =>
        `${item.name}|₹${item.price || 0}|sold:${item.quantity}×|rev:₹${item.revenue}|${item.quantity > 50 ? '🔥popular' : ''}`
      );
      sections.push(`POPULAR ITEMS:\n${itemLines.join('\n')}`);
    }

    // Format low stock items
    if (data.lowStockItems && data.lowStockItems.length > 0) {
      const stockLines = data.lowStockItems.map((item: any) =>
        `${item.name}|${item.currentStock}remaining|min:${item.minStock}|⚠low`
      );
      sections.push(`LOW STOCK ALERTS:\n${stockLines.join('\n')}`);
    }

    // Format metrics
    if (data.metrics) {
      const metrics = data.metrics;
      sections.push(`METRICS:\nToday:₹${metrics.todaySales || 0}(${metrics.todayOrders || 0}orders)|Yesterday:₹${metrics.yesterdaySales || 0}|Total:₹${metrics.totalRevenue || 0}|AvgOrder:₹${metrics.averageOrderValue || 0}`);
    }

    return sections.join('\n\n');
  }

  private maskPhone(phone: string): string {
    if (!phone) return 'N/A';
    return phone.length > 8 ? phone.slice(0, 4) + '****' : phone;
  }

  private compressItems(items: any[]): string {
    if (!items || items.length === 0) return 'No items';
    return items.slice(0, 3).map((item: any) => `${item.menuItemName}×${item.quantity || 1}`).join(',') + (items.length > 3 ? '...' : '');
  }

  private formatCompactDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  }

  async getBusinessData(tenantId: string): Promise<any> {
    try {
      
      // Get tenant data
      await getDoc(doc(db, 'tenants', tenantId));

      // Get today's orders
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayOrdersQuery = query(
        collection(db, 'tenants', tenantId, 'orders'),
        where('createdAt', '>=', today),
        where('createdAt', '<', tomorrow)
      );
      const todayOrdersSnapshot = await getDocs(todayOrdersQuery);
      const todayOrders = todayOrdersSnapshot.docs.map(doc => doc.data());

      // Get yesterday's orders
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayOrdersQuery = query(
        collection(db, 'tenants', tenantId, 'orders'),
        where('createdAt', '>=', yesterday),
        where('createdAt', '<', today)
      );
      const yesterdayOrdersSnapshot = await getDocs(yesterdayOrdersQuery);
      const yesterdayOrders = yesterdayOrdersSnapshot.docs.map(doc => doc.data());

      // Calculate metrics
      const todaySales = todayOrders.reduce((sum, order) => sum + (order.total || 0), 0);
      const yesterdaySales = yesterdayOrders.reduce((sum, order) => sum + (order.total || 0), 0);
      const totalRevenue = todaySales + yesterdaySales; // Simplified for demo

      // Get popular items (simplified)
      const popularItems = [
        { name: 'Coffee', quantity: 25, revenue: 1250 },
        { name: 'Sandwich', quantity: 15, revenue: 900 },
        { name: 'Cake', quantity: 8, revenue: 640 }
      ];

      // Get low stock items
      const lowStockItems = [
        { name: 'Milk', currentStock: 2, minStock: 5 },
        { name: 'Bread', currentStock: 1, minStock: 3 }
      ];

      // Get top customers
      const topCustomers = [
        { name: 'John Doe', totalSpent: 2500, orderCount: 5 },
        { name: 'Jane Smith', totalSpent: 1800, orderCount: 3 }
      ];

      const businessData = {
        todaySales,
        todayOrders: todayOrders.length,
        yesterdaySales,
        yesterdayOrders: yesterdayOrders.length,
        totalRevenue,
        averageOrderValue: todayOrders.length > 0 ? todaySales / todayOrders.length : 0,
        popularItems,
        lowStockItems,
        topCustomers,
        activeCustomers: 15,
        totalCustomers: 25,
        inventoryValue: 5000,
        yesterdayTopSpender: yesterdayOrders.length > 0 ? {
          name: 'John Doe',
          amount: 500
        } : null,
        unpaidCustomers: [],
        totalUnpaidAmount: 0,
        unpaidOrders: []
      };

      return businessData;

    } catch (error) {
      return {
        todaySales: 0,
        todayOrders: 0,
        yesterdaySales: 0,
        yesterdayOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        popularItems: [],
        lowStockItems: [],
        topCustomers: [],
        activeCustomers: 0,
        totalCustomers: 0,
        inventoryValue: 0
      };
    }
  }
}

// Export singleton instance
const firebaseAIService = new FirebaseAIService();
export default firebaseAIService; 